import React, { useEffect, useState } from "react";
import { IconButton, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(theme => ({
  // Container wrapper for the button and pulse effect
  wrapper: {
    position: "relative",
    display: "inline-block",
    zIndex: 1,
  },
  // Styles for the pulsing ring animation element
  pulseRing: {
    content: '""',
    position: "absolute",
    // Position the ring 4px outside the button on all sides
    top: -4,
    left: -4,
    right: -4,
    bottom: -4,
    borderRadius: "50%",
    background: theme.palette.secondary.main,
    opacity: 0, // Initially invisible
    transform: "scale(1)",
    transition: "all 1s ease-in-out",
    pointerEvents: "none", // Prevent ring from intercepting clicks
  },
  // Style applied during the pulse animation
  pulsing: {
    opacity: "0.5 !important", // Make ring visible
    transform: "scale(1.5) !important", // Expand ring
  },
  // Style applied as pulse fades out
  fadingOut: {
    opacity: "0 !important",
    transform: "scale(1.5) !important",
  },
  // Style for the actual button element
  button: {
    position: "relative",
    zIndex: 2, // Ensure button stays above pulse ring
  },
  // Style to hide the pulse effect after animation completes
  completed: {
    display: "none !important",
  },
}));

/**
 * PulsingIconButton Component
 * Creates an icon button with a pulsing animation effect
 *
 * @param {string} tooltip - Text to display in the tooltip
 * @param {string} id - Button identifier
 * @param {function} onClick - Click handler function
 * @param {React.ReactNode} children - Button content (typically an icon)
 * @param triggerPulse - Trigger pulse animation
 */
const PulsingIconButton = ({
  tooltip,
  id,
  onClick,
  children,
  triggerPulse = { show: false, timestamp: null },
}) => {
  const pulseCountNumber = 1; // Number of pulses to display
  const classes = useStyles();
  // State for managing pulse animation
  const [isPulsing, setIsPulsing] = useState(false);
  const [isCompleted, setIsCompleted] = useState(true);
  const [pulseCount, setPulseCount] = useState(0);
  const [lastPulseTimestamp, setLastPulseTimestamp] = useState(null);

  // Reset animation state when triggerPulse reference changes and is true
  useEffect(() => {
    if (!triggerPulse.show) {
      return;
    }

    if (triggerPulse.timestamp <= lastPulseTimestamp) {
      return;
    }

    // triggerPulse.show is true and triggerPulse.timestamp > lastPulseTimestamp,
    // it means the user just started a new report export.
    setLastPulseTimestamp(triggerPulse.timestamp);
    setIsPulsing(false);
    setIsCompleted(false);
    setPulseCount(0);
  }, [lastPulseTimestamp, triggerPulse]); // This effect will only run when triggerPulse's reference changes

  // Effect to control pulse animation timing
  useEffect(() => {
    if (!triggerPulse || isCompleted) return;

    // Start pulse animation after a 50ms delay
    const startPulse = () => {
      if (pulseCount < pulseCountNumber) { // Continue pulsing
        setIsPulsing(true);
      } else {
        setIsCompleted(true); // Hide pulse effect after "pulseCountNumber" pulses
      }
    };

    const timeoutId = setTimeout(startPulse, 50);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeoutId); // Cleanup timeout on component unmount
  }, [triggerPulse, isCompleted, pulseCount, pulseCountNumber]);

  // Handle completion of each pulse animation
  const handleTransitionEnd = () => {
    if (isPulsing) {
      setIsPulsing(false);
      setPulseCount(count => {
        const newCount = count + 1;
        if (newCount >= pulseCountNumber) {
          setIsCompleted(true);
        }
        return newCount;
      });
    }
  };

  return (
    <div className={classes.wrapper}>
      {/* Pulse animation ring */}
      <div
        className={`${classes.pulseRing} ${isPulsing ? classes.pulsing : ""} ${isCompleted ? classes.completed : ""}`}
        onTransitionEnd={handleTransitionEnd}
      />
      {/* Button with tooltip */}
      <Tooltip title={tooltip}>
        <IconButton
          id={id}
          color="inherit"
          onClick={onClick}
          className={classes.button}
        >
          {children}
        </IconButton>
      </Tooltip>
    </div>
  );
};

export default PulsingIconButton;
